import { CommentData } from "../../comment/interfaces/dto";
import {
	CommentListResponse,
	SubmitCommentResponseData,
} from "../../comment/interfaces/http";
import { fetchAllComments } from "../../comment/services/comment-service";
import setupAjaxComment from "../../comment/setup-ajax-comment";
import { PhotoData } from "../../photo/interfaces/dto";
import { PopupCommentData } from "../interfaces/dto";
import renderCommentData from "./render-comment-data";

const { findHtmlEl } = pm.utils.dom;

export default async function renderComments(
	popup: HTMLElement,
	photoData: PhotoData,
	commentListItemToClone: HTMLElement,
) {
	const commentsSection = findHtmlEl(popup, ".commentlist");
	if (!commentsSection) return;

	const commentListWrapper = findHtmlEl(commentsSection, ".comments");
	if (!commentListWrapper) return;

	const commentsTitleEl = findHtmlEl(popup, "#comments-title");
	if (!commentsTitleEl) return;

	const response: CommentListResponse = await fetchAllComments({
		action: "pm_fetch_all_comments",
		nonce: popup.dataset.fetchCommentsNonce
			? popup.dataset.fetchCommentsNonce
			: "",
		photoId: photoData.id,
	});

	let commentList: CommentData[] = [];

	if (!response.success) {
		commentsTitleEl.innerHTML = response.message ? response.message : "";
	} else {
		commentList = response.data;
	}

	if (commentList.length) {
		// Loop through the comments and render them.
		for (const comment of commentList) {
			renderCommentListItem(
				popup,
				commentListWrapper,
				photoData,
				comment,
				commentListItemToClone,
				1,
			);
		}
	}

	commentsSection.appendChild(commentListWrapper);

	window.addComment?.init?.(commentsSection);

	renderCommentsCount(countTotalCommentItems(commentList));
	setupAjaxComment(popup, handleOnCommentAdded);
	commentsSection.classList.remove("is-hidden");

	function renderCommentsCount(totalComments: number) {
		const commentsCount = findHtmlEl(popup, ".comments-count");
		if (!commentsCount) return;

		commentsCount.textContent = totalComments.toString();
		const commentsTitle = commentsCount.parentElement;

		if (!totalComments) {
			commentsTitle?.classList.add("is-hidden");
		} else {
			commentsTitle?.classList.remove("is-hidden");
		}
	}

	function countTotalCommentItems(commentList: CommentData[]): number {
		let totalCount = 0;

		for (const item of commentList) {
			totalCount++; // Count the current item

			if (item.children && item.children.length > 0) {
				// Count nested items if any, recursively
				totalCount += countTotalCommentItems(item.children);
			}
		}

		return totalCount;
	}

	function handleOnCommentAdded(
		postId: number,
		parentId: number,
		responseData: SubmitCommentResponseData,
	) {
		if (parentId) {
			const respond = findHtmlEl(popup, ".comment-respond");

			if (respond) {
				// Move respond element to be after a section with "commentlist" class using insertBefore.
				commentListWrapper!.parentNode?.insertBefore(
					respond,
					commentListWrapper!.nextElementSibling,
				);
			}
		}

		pm.utils.dom.emptyElement(commentListWrapper!);
		renderCommentsCount(countTotalCommentItems(responseData.commentList));

		if (!responseData.commentList.length) return;

		// Loop through the comments and render them.
		for (const comment of responseData.commentList) {
			renderCommentListItem(
				popup,
				commentListWrapper!,
				photoData,
				comment,
				commentListItemToClone,
				1,
			);
		}

		if (parentId) {
			// Move the div with id "respond" to the initial place which is after a section with class "commentlist".
			window?.addComment?.moveForm?.(
				`comment-${parentId}`,
				parentId,
				"respond",
				postId,
				undefined,
			);
		}
	}
}

function renderCommentListItem(
	popup: HTMLElement,
	commentListWrapper: HTMLElement,
	photoData: PhotoData,
	commentData: CommentData,
	itemToClone: HTMLElement,
	depth?: number,
) {
	const isCommentAuthor =
		pm.currentUser.isLoggedIn && commentData.authorId === pm.currentUser.id;

	const commentDate = new Date(commentData.dateGmt);

	const formattedDate = commentDate.toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
	});

	const popupCommentData: PopupCommentData = {
		commentReportingNonce: popup.dataset.reportCommentNonce
			? popup.dataset.reportCommentNonce
			: "",
		loginUrl: pm.loginUrl,
		photoAttachmentUrl: photoData.attachmentUrl,
		photoId: photoData.id,
		isLoggedIn: pm.currentUser.isLoggedIn,
		isCommentAuthor: isCommentAuthor,
		inlineEditingClass: isCommentAuthor ? "inline-editing" : "",
		isEditableClass: isCommentAuthor ? "is-content-editable" : "",
		commentId: commentData.id,
		commentDate: commentData.date,
		commentDateGmt: commentData.dateGmt,
		commentFormattedDate: formattedDate,
		commentAuthorId: commentData.authorId,
		commentAuthorNicename: commentData.authorNicename,
		commentAuthorDisplayName: commentData.authorDisplayName,
		commentAuthorProfileUrl: commentData.authorProfileUrl,
		commentAuthorAvatarUrl: commentData.authorAvatarUrl,
		commentAuthorMembershipType: "",
		commentContent: commentData.content,
		commentFormattedContent: pm.utils.str.wpautop(commentData.content, true),
		commentIsLiked: commentData.isLiked,
		commentTotalLikes: commentData.totalLikes,
	};

	const commentListItemEl = itemToClone.cloneNode(true) as HTMLElement;

	if (depth && depth > 2) {
		const replyLink = findHtmlEl(commentListItemEl, ".comment-reply-link");

		if (replyLink) {
			// Remove the Reply link.
			replyLink.parentNode?.removeChild(replyLink);
		}
	}

	const commentArticle = findHtmlEl(commentListItemEl, "article.comment");

	if (commentArticle) {
		commentArticle.classList.remove("depth-1");
		commentArticle.classList.add(`depth-${depth}`);
	}

	renderCommentData(commentListWrapper, commentListItemEl, popupCommentData);

	if (commentData.children.length > 0) {
		for (const childComment of commentData.children) {
			// Create ul element for the children.
			const childrenListEl = document.createElement("ul");
			childrenListEl.classList.add("children");

			renderCommentListItem(
				popup,
				childrenListEl,
				photoData,
				childComment,
				itemToClone,
				(depth ? depth : 1) + 1,
			);

			commentListItemEl.appendChild(childrenListEl);
		}
	}
}
